import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { FaShoppingCart } from "react-icons/fa";
import { addToCart } from "../../../redux/shopSlice";
import { useTranslation } from "react-i18next";

const ProductInfo = ({ productInfo }) => {
  const dispatch = useDispatch();
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [quantityAvailable, setQuantityAvailable] = useState(0);
  const [cartError, setCartError] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const { t } = useTranslation();

  if (!productInfo || !productInfo.colors) {
    return <p>Loading product details...</p>;
  }

  const handleColorChange = (color) => {
    setSelectedColor(color);
    setSelectedSize(null);
    setQuantity(1);
    setQuantityAvailable(0);
    setCartError(null);
    setAddedToCart(false);
  };

  const handleSizeChange = (e) => {
    if (!selectedColor) return;
    const chosenSize = selectedColor.sizes.find(
      (size) => size.size === e.target.value
    );
    setSelectedSize(chosenSize);
    setQuantityAvailable(chosenSize.quantity);
    setQuantity(1);
    setCartError(null);
    setAddedToCart(false);
  };

  const handleQuantityChange = (e) => {
    const newQuantity = Math.min(Number(e.target.value), quantityAvailable);
    setQuantity(newQuantity);
    setCartError(null);
    setAddedToCart(false);
  };

  const handleAddToCart = () => {
    if (!selectedColor) {
      setAddedToCart(false);
      setCartError("selectColorError");
      return;
    }
    if (!selectedSize) {
      setAddedToCart(false);
      setCartError("selectSizeError");
      return;
    }
    if (!quantity || quantity < 1) {
      setAddedToCart(false);
      setCartError("selectQuantityError");
      return;
    }
    if (quantityAvailable === 0 || quantityAvailable < quantity) {
      setAddedToCart(false);
      setCartError("productN");
      return;
    }

    setCartError(null);
    dispatch(
      addToCart({
        _id: productInfo._id,
        productName: productInfo.productName,
        des: productInfo.des,
        quantity: quantity,
        image: productInfo.img,
        images: productInfo.images,
        badge: productInfo.badge,
        price: productInfo.price,
        colors: productInfo.colors,
        color: selectedColor.color_name,
        size: selectedSize.size,
        gender: productInfo.gender,
        season: productInfo.season,
        discounted_price: productInfo.discounted_price
      })
    );
    setAddedToCart(true);
  };

  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-4xl font-poppins font-medium">{productInfo.productName}</h2>
      {productInfo.promo_percentage !== "0.00" ? (
        <>
          <p>
            <span className="text-primeColor font-bold line-through">{productInfo.price} TND</span>
            <span className="ml-6 text-primeColor font-bold">
              {productInfo.discounted_price} TND
            </span>
          </p>
        </>
      ) : (
        <p className="text-xl font-semibold">{productInfo.price} TND</p>
      )}
      <p className="text-base text-gray-600">{productInfo.des}</p>
      
      {productInfo.gender === "Women" && (
        <p
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            color: "#ff69b4",
            textShadow: "2px 2px 4px rgba(199, 179, 179, 0.3)",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <strong>{t("unique")}</strong>
        </p>
      )}
      
      {productInfo.gender === "Men" && (
        <p
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            color: "#708090",
            textShadow: "2px 2px 4px rgba(0, 0, 0, 0.3)",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <strong>{t("stand")}</strong>
        </p>
      )}
      {productInfo.gender === "Accessoire" && (
        <p
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            color: "#708090",
            textShadow: "2px 2px 4px rgba(0, 0, 0, 0.3)",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <strong>{t("stand")}</strong>
        </p>
      )}


      <div className="mt-4">
        <label className="font-semibold block mb-2">{t("SelectC")}:</label>
        <ul className="flex gap-2 flex-wrap">
          {productInfo.colors.map((color, index) => (
            <li 
              key={index}
              onClick={() => handleColorChange(color)}
              className={`w-8 h-8 rounded-full border-2 cursor-pointer transition-all duration-200 ${
                selectedColor?.color_name === color.color_name 
                  ? 'border-black scale-110' 
                  : 'border-gray-300 hover:border-gray-500'
              }`}
              style={{ backgroundColor: color.color_name }}
              title={color.color_name}
            ></li>
          ))}
        </ul>
      </div>

      {selectedColor && (
        <div className="mt-4">
          <label className="font-semibold">{t("Selects")}: </label>
          <select 
            onChange={handleSizeChange} 
            value={selectedSize?.size || ""}
            className="p-2 border rounded"
          >
            <option value="" disabled>{t("Selects")}</option>
            {selectedColor.sizes.map((size) => (
              <option key={size.size} value={size.size}>
                {size.size} {size.quantity > 0 ? `` : "(out of stock)"}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedSize && (
        <div className="mt-4">
          <label className="font-semibold">{t("Selectq")}: </label>
          <input
            type="number"
            value={quantity}
            onChange={handleQuantityChange}
            min="1"
            max={quantityAvailable}
            className="w-20 p-2 border rounded"
          />
          {quantityAvailable > 0 && (
            <span className="ml-2 text-sm text-gray-500">
              Max: {quantityAvailable}
            </span>
          )}
        </div>
      )}

      {cartError && (
        <p className="text-red-500 text-sm font-medium">{t(cartError)}</p>
      )}

      <button
        onClick={handleAddToCart}
        className="w-full py-4 bg-primeColor hover:bg-black duration-300 text-white text-lg font-titleFont"
      >
        {t("Tocart")}
      </button>

      {addedToCart && (
        <>
          <p className="text-green-600 text-sm font-medium">{t("addedToCartSuccess")}</p>
          <Link
            to="/cart"
            className="w-full py-4 flex items-center justify-center gap-3 border-2 border-primeColor text-primeColor hover:bg-primeColor hover:text-white duration-300 text-lg font-titleFont"
          >
            <FaShoppingCart className="text-xl" />
            {t("Cart")}
          </Link>
        </>
      )}
    </div>
  );
};

export default ProductInfo;
